import { api } from 'api';
import { actionSelectOptions } from 'api/endpoints/actions-select-options';
import {
  ContractActionName,
  ServerContractActionName,
} from 'api/endpoints/contractActionNames';
import { ResponseType } from 'api/jsonFetch/jsonFetch.types';
import { FetchStatus } from 'types/fetch-status.types';
import {
  actionNamesError,
  contractActionNameCreated,
  contractActionNameEdit,
  contractActionNamesDone,
  contractActionNamesFetching,
} from '../actionNamesReducer';
import {
  contractActionSelectOptionsDone,
  contractActionSelectOptionsError,
  contractActionSelectOptionsFetching,
} from '../contractActionsSelectOptions';
import { AppDispatch, GetState } from '../store';

export const fetchContractActionsSelectOptions =
  () => async (dispatch: AppDispatch, _getState: GetState) => {
    dispatch(contractActionSelectOptionsFetching());
    const result = await actionSelectOptions.getContractOptions();
    if (result.type === ResponseType.OK) {
      return dispatch(contractActionSelectOptionsDone(result.data));
    }
    dispatch(
      contractActionSelectOptionsError({
        message: 'Could not fetch action options.',
      }),
    );
  };

function serverContractActionNameToActionName(
  sa: ServerContractActionName,
): ContractActionName {
  return {
    id: sa.id,
    hearingDate: sa.hearingDate,
    contractActionName: sa.contractActionName,
    contractActionNameId: sa.contractActionNameId,
    contractExhibitTypeId: sa.contractExhibitTypeId,
  };
}

export const fetchContractActionNamesThunk =
  () => async (dispatch: AppDispatch, _getState: GetState) => {
    dispatch(contractActionNamesFetching());
    const result = await api.contractActionNames.get();
    if (result.type === ResponseType.OK) {
      dispatch(
        contractActionNamesDone(
          result.data.map(serverContractActionNameToActionName),
        ),
      );
    } else {
      dispatch(actionNamesError({ message: 'Could not fetch resolution names.' }));
    }

    return result;
  };

export type CreateParams = {
  name: string;
  typeId: number;
  hearingDate?: string;
};

export type EditParams = CreateParams & { id: number };

export type ContractActionEditOrAddResponse =
  | {
      type: FetchStatus.Done;
      item: ContractActionName;
    }
  | {
      type: FetchStatus.Error;
      error: { message: string };
    };

export const createContractActionNameThunk =
  (
    newActionName: Omit<
      ContractActionName,
      | 'contractActionSubStatusId'
      | 'id'
      | 'contractActionTypeId'
      | 'contractActionNameId'
    >,
  ) =>
  async (
    dispatch: AppDispatch,
    _getState: GetState,
  ): Promise<ContractActionEditOrAddResponse> => {
    const result = await api.contractActionNames.post({
      contractExhibitTypeId: newActionName.contractExhibitTypeId,
      contractActionName: newActionName.contractActionName,
      hearingDate: newActionName.hearingDate,
    });
    if (result.type === ResponseType.OK) {
      const action = serverContractActionNameToActionName(result.data);
      dispatch(contractActionNameCreated(action));
      return { type: FetchStatus.Done, item: action };
    } else if (result.type === ResponseType.BAD_REQ) {
      const message = result.data.error[0].errorMessage;
      return { type: FetchStatus.Error, error: { message } };
    }
    return {
      type: FetchStatus.Error,
      error: {
        message: 'An unexpected error occurred when creating the resolution name.',
      },
    };
  };

export const editAContractsctionNameThunk =
  (
    editedAction: Omit<
      ContractActionName,
      'contractActionSubStatusId' | 'contractActionTypeId' | 'contractActionNameId'
    >,
  ) =>
  async (
    dispatch: AppDispatch,
    _getState: GetState,
  ): Promise<ContractActionEditOrAddResponse> => {
    const result = await api.contractActionNames.put({
      contractExhibitTypeId: editedAction.contractExhibitTypeId,
      contractActionName: editedAction.contractActionName,
      hearingDate: editedAction.hearingDate,
      id: editedAction.id,
    });
    if (result.type === ResponseType.OK) {
      const action = serverContractActionNameToActionName(result.data);
      dispatch(contractActionNameEdit(action));
      return { type: FetchStatus.Done, item: action };
    } else if (result.type === ResponseType.BAD_REQ) {
      // pick the first message
      const message = result.data.error[0].errorMessage;
      return { type: FetchStatus.Error, error: { message } };
    }
    return {
      type: FetchStatus.Error,
      error: {
        message: 'An unexpected error occured when editing the resolution name.',
      },
    };
  };
